<template>
  <b-modal
    :title="helper.trans('labels.password')"
    :modelValue="target !== null"
    @update:modelValue="(open) => !open && hide()"
  >
    <template #default>
      <LoaderWrapper ref="loader">
        <FormInputWrapper v-slot="parentProps" :errors="getErrors('plainPassword')">
          <div class="input-group" :class="parentProps.class">
            <input v-model="password" :class="parentProps.class" type="text" class="form-control" id="change-password-input">
            <button @click.prevent="password = helper.generatePassword()" type="button" class="btn btn-outline-secondary"><i class="fa fa-rotate"></i></button>
          </div>
        </FormInputWrapper>
      </LoaderWrapper>
    </template>
    <template #footer>
      <button type="button" class="btn btn-outline-secondary btn-sm text-capitalize-first" @click="hide">{{ helper.trans('button.cancel') }}</button>
      <button type="button" class="btn btn-outline-primary btn-sm text-capitalize-first" @click="save">{{ helper.trans('button.save') }}</button>
    </template>
  </b-modal>
</template>

<script lang="ts">
import {defineComponent, ref} from 'vue';
import {BModal} from 'bootstrap-vue-3';
import {helper} from "@/service/CommonHelper";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import FormInputWrapper from "@/components/FormInputWrapper.vue";
import ValidationErrorDTO from "@/models/ValidationErrorDTO";
import {useNotification} from "@kyvg/vue3-notification";

const notification = useNotification()

type ChangePasswordFn = (id: number, plainPassword: string) => Promise<void>;

export default defineComponent({
  components: {
    BModal,
    LoaderWrapper,
    FormInputWrapper,
  },
  emits: ['updated'],
  setup(_, {emit}) {
    const loader = ref<InstanceType<typeof LoaderWrapper> | null>(null)
    const target = ref<{id: number, changeFn: ChangePasswordFn} | null>(null)
    const password = ref('')
    const errors = ref<Record<string, ValidationErrorDTO[]>>({})

    const getErrors = (path: string) => errors.value[path] ?? []

    const show = (id: number, changeFn: ChangePasswordFn) => {
      target.value = {id, changeFn}
      password.value = ''
      errors.value = {}
    }

    const hide = () => {
      target.value = null
      password.value = ''
      errors.value = {}
    }

    const save = async () => {
      if (!target.value) {
        return
      }
      errors.value = {}
      loader.value?.show()
      try {
        await target.value.changeFn(target.value.id, password.value)
        notification.notify({title: helper.trans('labels.password_changed')})
        hide()
        emit('updated')
      } catch (e: any) {
        if (e.response && e.request && e.request.status === 400 && e.response.data && Array.isArray(e.response.data)) {
          const next: Record<string, ValidationErrorDTO[]> = {}
          for (const err of e.response.data) {
            if (!next[err.field]) {
              next[err.field] = []
            }
            next[err.field].push(err)
          }
          errors.value = next
        } else {
          console.log(e)
        }
      } finally {
        loader.value?.hide()
      }
    }

    return {
      helper,
      loader,
      password,
      getErrors,
      show,
      hide,
      save,
      target,
    }
  }
})
</script>
